/**
 * The backend speaks RFC 9457 problem+json. This turns one into something a UI can branch on.
 *
 * Always branch on `code`, never on `detail` — detail is prose written for a person, and it will
 * be reworded or localized without warning. `code` is the contract.
 */

export interface ProblemFieldError {
  readonly field: string
  readonly message: string
}

export interface Problem {
  readonly type?: string
  readonly title?: string
  readonly status?: number
  readonly detail?: string
  readonly code?: string
  readonly errors?: readonly ProblemFieldError[]
}

export class ApiError extends Error {
  readonly status: number
  readonly code: string
  readonly detail: string | undefined
  readonly fieldErrors: readonly ProblemFieldError[]

  constructor(status: number, problem: Problem) {
    super(problem.detail ?? problem.title ?? `Request failed with ${String(status)}`)
    this.name = 'ApiError'
    this.status = status
    this.code = problem.code ?? codeForStatus(status)
    this.detail = problem.detail
    this.fieldErrors = problem.errors ?? []
  }

  /** The session is gone or was never there. The app must return to the sign-in screen. */
  get isUnauthenticated(): boolean {
    return this.status === 401
  }

  /** Signed in, but not allowed. Never retry one of these. */
  get isForbidden(): boolean {
    return this.status === 403
  }

  get isNotFound(): boolean {
    return this.status === 404
  }

  /** A 5xx, or a network failure that never reached the server. Retrying can be reasonable. */
  get isTransient(): boolean {
    return this.status >= 500 || this.status === 0
  }

  /** The server rejected the shape of the request; the fields say where. */
  get isValidation(): boolean {
    return this.status === 422 || this.fieldErrors.length > 0
  }
}

/** The request never reached the server — DNS, offline, CORS, a dropped connection. */
export class NetworkError extends ApiError {
  constructor(cause: unknown) {
    super(0, {
      code: 'NETWORK_UNREACHABLE',
      detail: 'Could not reach the server.',
    })
    this.name = 'NetworkError'
    this.cause = cause
  }
}

function codeForStatus(status: number): string {
  switch (status) {
    case 400:
      return 'MALFORMED_REQUEST'
    case 401:
      return 'UNAUTHENTICATED'
    case 403:
      return 'FORBIDDEN'
    case 404:
      return 'ROUTE_NOT_FOUND'
    case 409:
      return 'ALREADY_EXISTS'
    case 413:
      return 'PAYLOAD_TOO_LARGE'
    case 422:
      return 'VALIDATION_FAILED'
    case 429:
      return 'RATE_LIMITED'
    case 503:
      return 'SERVICE_UNAVAILABLE'
    default:
      return status >= 500 ? 'INTERNAL_ERROR' : 'REQUEST_FAILED'
  }
}

export function isApiError(value: unknown): value is ApiError {
  return value instanceof ApiError
}
